import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2, ClipboardCheck, FileText } from 'lucide-react';

/**
 * ReadyToQuoteBanner — Components-tab banner that runs through the four
 * "ready to quote" gates and prompts the operator to finish anything still
 * outstanding before generating the Quote PDF.
 *
 * Calls `GET /api/appraisals/{id}/ready-to-quote`.
 */
export default function ReadyToQuoteBanner({ api, appraisalId, onJumpTab, onGenerateQuote, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      const res = await api.get(`/appraisals/${appraisalId}/ready-to-quote`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading || !data) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-500 flex items-center gap-2" data-testid="ready-to-quote-loading">
        <Loader2 size={12} className="animate-spin" /> Checking quote-readiness…
      </div>
    );
  }

  const gates = data.gates || [];
  const done = gates.filter((g) => g.done).length;
  const ready = !!data.ready;

  // Map gate keys to the tab that resolves them, so each row gets a
  // "Fix" shortcut linking the operator to the right place.
  const gateTabMap = {
    roof_details: 'Roof Details',
    master_plan:  'Diagrams',
    have_line:    'Components',
    priced:       'Components',
    inspection_generated: 'Roof Appraisal',
  };

  return (
    <div
      className={`rounded-md border p-3 ${ready ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
      data-testid="ready-to-quote-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`p-2 rounded ${ready ? 'bg-emerald-100' : 'bg-amber-100'}`}>
          {ready ? <ClipboardCheck size={18} className="text-emerald-700" /> : <AlertTriangle size={18} className="text-amber-700" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-[#0F172A]">
              {ready ? 'Ready to quote' : 'Almost ready'}
            </h3>
            <span className="text-[10px] font-mono-num text-slate-600" data-testid="ready-to-quote-counter">
              {done} / {gates.length} gates passed
            </span>
          </div>
          <ul className="mt-2 space-y-1">
            {gates.map((g) => {
              const tab = gateTabMap[g.key];
              return (
                <li
                  key={g.key}
                  data-testid={`ready-gate-${g.key}`}
                  className="text-[12px] flex items-start gap-2"
                >
                  {g.done
                    ? <CheckCircle2 size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    : <AlertTriangle size={13} className="text-amber-700 flex-shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <span className={`font-semibold ${g.done ? 'text-emerald-800 line-through decoration-emerald-300/60' : 'text-amber-900'}`}>{g.label}</span>
                    {!g.done && g.hint && <span className="text-slate-600 ml-1">— {g.hint}</span>}
                    {!g.done && tab && typeof onJumpTab === 'function' && (
                      <button
                        type="button"
                        onClick={() => onJumpTab(tab)}
                        className="ml-2 text-[10px] font-bold text-[#D35400] hover:underline"
                      >
                        Jump to {tab} ›
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {ready && typeof onGenerateQuote === 'function' && (
            <button
              type="button"
              data-testid="ready-to-quote-generate-btn"
              onClick={onGenerateQuote}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md shadow-sm transition-colors"
            >
              <FileText size={12} /> Generate Quote PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
